import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectSupportWindowOpenForOrg } from '@/reducers/supportAccess/supportAccess.redux'

/**
 * Hook to determine if the current view should hide customer ID data (voip_customer_id).
 *
 * Returns true (hide customer ID) when:
 * - Viewing a redacted org's data that doesn't have unredacted_customer_id_enabled
 *
 * Returns false (show customer ID) when:
 * - Org has unredacted_enabled (full unredacted)
 * - Org has unredacted_customer_id_enabled (redacted org with unredacted customer ID)
 * - Balto admin viewing a redacted org with unredacted customer ID (allowed to see customer ID)
 * - Balto admin AND an active support access window is open for the viewed org
 *
 * Note: Balto admin cannot view customer ID for full unredacted orgs (backend restriction)
 * unless a support access window is open *for that specific org*.
 *
 * @param {number|object} selectedOrgId - The org ID being viewed, or an object with { value: orgId }
 * @returns {boolean} - Whether to hide customer ID columns/data
 */
export const useShouldHideCustomerId = (selectedOrgId) => {
  const {
    unredacted_enabled: isCurrentOrgUnredacted,
    unredacted_customer_id_enabled: isCurrentOrgCustomerIdUnredacted,
    id: currentUserOrgId,
  } = useSelector((state) => state.currentOrganization)
  const { organizations } = useSelector((state) => state)

  // Resolve the org being viewed (handle both direct ID and { value: id } format).
  const orgId = typeof selectedOrgId === 'object' ? selectedOrgId?.value : selectedOrgId
  // QCER-802: only honor the support-access window when it belongs to the viewed
  // org; the slice tracks `windowOrgId` so a stale Org A fetch can't unmask Org B.
  const supportWindowOpenForViewedOrg = useSelector((state) =>
    selectSupportWindowOpenForOrg(state, orgId)
  )

  return useMemo(() => {
    const isBaltoAdmin = currentUserOrgId === 1
    const supportWindowOpen = isBaltoAdmin && supportWindowOpenForViewedOrg

    // Helper to check if org can show customer ID
    const canShowCustomerId = (org) =>
      org?.unredacted_enabled || org?.unredacted_customer_id_enabled

    if (orgId) {
      // Balto admins can see customer ID for redacted orgs with unredacted customer ID,
      // but not for full unredacted orgs — unless a support access window is open
      if (isBaltoAdmin && orgId !== 1) {
        if (supportWindowOpen) {
          return false
        }
        const selectedOrg = organizations?.find((org) => org.id === orgId)
        // Block if full unredacted, allow if redacted org with unredacted customer ID
        if (selectedOrg?.unredacted_enabled) {
          return true
        }
        return !selectedOrg?.unredacted_customer_id_enabled
      }
      // If viewing own org, use current org's status directly
      if (orgId === currentUserOrgId) {
        return !(isCurrentOrgUnredacted || isCurrentOrgCustomerIdUnredacted)
      }
      // Check selected org's redaction status
      const selectedOrg = organizations?.find((org) => org.id === orgId)
      // Fallback to current org settings if selected org not found (hierarchy managers)
      if (!selectedOrg) {
        return !(isCurrentOrgUnredacted || isCurrentOrgCustomerIdUnredacted)
      }
      return !canShowCustomerId(selectedOrg)
    }
    return !(isCurrentOrgUnredacted || isCurrentOrgCustomerIdUnredacted)
  }, [
    orgId,
    organizations,
    currentUserOrgId,
    isCurrentOrgUnredacted,
    isCurrentOrgCustomerIdUnredacted,
    supportWindowOpenForViewedOrg,
  ])
}
